import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: API,
  timeout: 30000, // 30 second timeout
});

// Add response interceptor for global error handling
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Log error for debugging
    console.error('API Error:', {
      url: error.config?.url,
      status: error.response?.status,
      message: error.response?.data?.detail || error.message
    });
    
    // Handle auth errors - but DON'T auto-redirect
    // Let individual components handle their own auth errors
    if (error.response?.status === 401) {
      const isAuthEndpoint = error.config?.url?.includes('/auth/');
      if (!isAuthEndpoint) {
        console.warn('Auth error detected on:', error.config?.url);
        // DON'T clear token or redirect here - let the component decide
        // This prevents random logouts from transient errors
      }
    }
    
    return Promise.reject(error);
  }
);

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const api = {
  // Auth
  register: (data) => axiosInstance.post(`/auth/register`, data),
  login: (data) => axiosInstance.post(`/auth/login`, data),
  verifyOTP: (data) => axiosInstance.post(`/auth/verify-otp`, data),
  getMe: () => axiosInstance.get(`/auth/me`, { headers: getAuthHeader(), withCredentials: true }),
  logout: () => axiosInstance.post(`/auth/logout`, {}, { withCredentials: true }),

  // Dashboard
  getIndividualDashboard: () => axiosInstance.get(`/dashboard/individual`, { headers: getAuthHeader() }),
  getTodayActions: () => axiosInstance.get(`/rep/today-actions`, { headers: getAuthHeader() }),
  getCityDashboard: (city) => axiosInstance.get(`/dashboard/city?city=${city}`, { headers: getAuthHeader() }),
  getPanIndiaDashboard: () => axiosInstance.get(`/dashboard/pan-india`, { headers: getAuthHeader() }),

  // Analytics
  getAdminAnalytics: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/admin/analytics?${query}`, { headers: getAuthHeader() });
  },
  getAnalyticsTrends: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/admin/analytics/trends?${query}`, { headers: getAuthHeader() });
  },

  // Leaderboard
  getRegularLeaderboard: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/leaderboard/regular?${query}`, { headers: getAuthHeader() });
  },
  getActiveContest: () => axiosInstance.get(`/leaderboard/active-contest`, { headers: getAuthHeader() }),
  
  // Profile
  uploadProfilePhoto: (formData) => axiosInstance.post(`/auth/profile/photo`, formData, { 
    headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
  }),
  removeProfilePhoto: () => axiosInstance.delete(`/auth/profile/photo`, { headers: getAuthHeader() }),
  getProfileStats: () => axiosInstance.get(`/auth/profile/stats`, { headers: getAuthHeader() }),
  updateProfileBio: (bio) => axiosInstance.put(`/auth/profile/bio?bio=${encodeURIComponent(bio)}`, null, { headers: getAuthHeader() }),
  
  // Contests
  getContests: () => axiosInstance.get(`/contests`, { headers: getAuthHeader() }),
  getContestLeaderboard: (contestId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/contests/${contestId}/leaderboard?${query}`, { headers: getAuthHeader() });
  },

  // Incentives
  calculateIncentive: (params) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/incentive/calculate?${query}`, { headers: getAuthHeader() });
  },
  getSlabs: () => axiosInstance.get(`/incentive/slabs`, { headers: getAuthHeader() }),
  createSlab: (data) => axiosInstance.post(`/incentive/slabs`, data, { headers: getAuthHeader() }),
  deleteSlab: (slabId) => axiosInstance.delete(`/incentive/slabs/${slabId}`, { headers: getAuthHeader() }),

  // EMI
  calculateEMI: (params) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/emi/calculate?${query}`);
  },

  // Projects
  getProjects: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/projects?${query}`, { headers: getAuthHeader() });
  },
  getActiveProjects: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/projects-active?${query}`, { headers: getAuthHeader() });
  },
  searchProjects: (searchQuery, city = null, limit = 50) => {
    const params = { q: searchQuery, limit };
    if (city) params.city = city;
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/projects/search?${query}`, { headers: getAuthHeader() });
  },
  getLocationsByCity: (city) => axiosInstance.get(`/locations-by-city?city=${encodeURIComponent(city)}`, { headers: getAuthHeader() }),
  getProjectDetail: (projectId) => axiosInstance.get(`/projects/${projectId}`, { headers: getAuthHeader() }),
  getProjectTeamActivity: (projectId, days = 30) => axiosInstance.get(`/projects/${projectId}/team-activity?days=${days}`, { headers: getAuthHeader() }),
  getProjectPricing: (projectId) => axiosInstance.get(`/projects/${projectId}/pricing`, { headers: getAuthHeader() }),
  getProjectSpocs: (projectId) => axiosInstance.get(`/projects/${projectId}/spocs`, { headers: getAuthHeader() }),
  createProject: (data) => axiosInstance.post(`/projects`, data, { headers: getAuthHeader() }),
  updateProject: (projectId, data) => axiosInstance.put(`/projects/${projectId}`, data, { headers: getAuthHeader() }),
  archiveProject: (projectId) => axiosInstance.delete(`/projects/${projectId}`, { headers: getAuthHeader() }),
  importProjectsCSV: (projects) => axiosInstance.post(`/projects/import-csv`, { projects }, { headers: getAuthHeader() }),
  
  // Smart Project Sharing
  getProjectShareData: (projectId) => axiosInstance.get(`/projects/${projectId}/share-data`, { headers: getAuthHeader() }),
  generateProjectUSPs: (projectId, forceRegenerate = false) => 
    axiosInstance.post(`/projects/${projectId}/generate-usps`, { project_id: projectId, force_regenerate: forceRegenerate }, { headers: getAuthHeader() }),
  updateProjectUSPs: (projectId, usps, markVerified = false) => 
    axiosInstance.put(`/projects/${projectId}/usps`, { usps, mark_verified: markVerified }, { headers: getAuthHeader() }),
  generateShareMessage: (projectId, customerId = null, mode = 'detailed', useAiIntro = false) => 
    axiosInstance.post(`/projects/${projectId}/generate-share-message`, { 
      project_id: projectId, 
      customer_id: customerId, 
      mode, 
      use_ai_intro: useAiIntro 
    }, { headers: getAuthHeader() }),

  // Bookings
  createBooking: (data) => axiosInstance.post(`/bookings`, data, { headers: getAuthHeader() }),
  getMyBookings: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/bookings/my?${query}`, { headers: getAuthHeader() });
  },
  getPendingBookings: () => axiosInstance.get(`/bookings/pending`, { headers: getAuthHeader() }),
  getAllBookings: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/bookings/all?${query}`, { headers: getAuthHeader() });
  },
  approveBooking: (bookingId, remark = null) => axiosInstance.post(`/bookings/${bookingId}/approve`, { remark }, { headers: getAuthHeader() }),
  rejectBooking: (bookingId, reason) => axiosInstance.post(`/bookings/${bookingId}/reject`, { reason }, { headers: getAuthHeader() }),
  getBookingAuditTrail: (bookingId) => axiosInstance.get(`/bookings/${bookingId}/audit`, { headers: getAuthHeader() }),
  getEligibleProjectsForBooking: (customerId) => axiosInstance.get(`/bookings/eligible-projects/${customerId}`, { headers: getAuthHeader() }),

  // Booking Edit Requests
  createBookingEditRequest: (data) => axiosInstance.post(`/bookings/edit-request`, data, { headers: getAuthHeader() }),
  getBookingEditRequests: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/bookings/edit-requests?${query}`, { headers: getAuthHeader() });
  },
  resolveBookingEditRequest: (editRequestId, data) => 
    axiosInstance.put(`/bookings/edit-request/${editRequestId}/resolve`, data, { headers: getAuthHeader() }),
  getPendingEditRequestsCount: () => axiosInstance.get(`/bookings/edit-requests/pending-count`, { headers: getAuthHeader() }),

  // Booking Verification Module
  createVerifiedBooking: (data) => axiosInstance.post(`/bookings/verified`, data, { headers: getAuthHeader() }),
  getVerificationList: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/bookings/verification-list?${query}`, { headers: getAuthHeader() });
  },
  bookingVerifyAction: (bookingId, action, remark = null) => 
    axiosInstance.post(`/bookings/${bookingId}/verify-action?action=${action}`, null, { 
      headers: getAuthHeader(),
      params: remark ? { remark } : {}
    }),
  getVerificationStats: () => axiosInstance.get(`/bookings/verification-stats`, { headers: getAuthHeader() }),
  uploadBookingDocument: (formData, docType) => 
    axiosInstance.post(`/upload/booking-document?doc_type=${docType}`, formData, { 
      headers: getAuthHeader()  // Don't set Content-Type - axios will set it automatically with boundary for FormData
    }),

  // Booking Milestone Funnel
  getBookingDetail: (bookingId) => axiosInstance.get(`/bookings/${bookingId}/detail`, { headers: getAuthHeader() }),
  updateBookingMilestone: (bookingId, data) => axiosInstance.post(`/bookings/${bookingId}/milestone`, data, { headers: getAuthHeader() }),
  clearBookingMilestone: (bookingId, milestone) => axiosInstance.delete(`/bookings/${bookingId}/milestone/${milestone}`, { headers: getAuthHeader() }),
  getFunnelStats: (city = null) => {
    const params = city ? `?city=${city}` : '';
    return axiosInstance.get(`/bookings/funnel-stats${params}`, { headers: getAuthHeader() });
  },
  
  // Booking Documents (Version Controlled)
  uploadBookingDocumentV2: (bookingId, data) => axiosInstance.post(`/bookings/${bookingId}/documents`, data, { headers: getAuthHeader() }),
  getBookingDocuments: (bookingId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/bookings/${bookingId}/documents?${query}`, { headers: getAuthHeader() });
  },
  getDocumentVersionHistory: (bookingId, docType) => axiosInstance.get(`/bookings/${bookingId}/documents/${docType}/history`, { headers: getAuthHeader() }),

  // Admin
  seedData: () => axiosInstance.post(`/seed-data`),
  getAccountabilitySnapshot: () => axiosInstance.get(`/admin/accountability-snapshot`, { headers: getAuthHeader() }),

  // ==================== Auction Supply (Admin-Only) ====================
  getAuctionSupply: (params = {}) => {
    const query = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([_, v]) => v != null && v !== ''))
    ).toString();
    return axiosInstance.get(`/admin/supply/auctions?${query}`, { headers: getAuthHeader() });
  },
  getAuctionSupplyStats: () => axiosInstance.get(`/admin/supply/auctions/stats`, { headers: getAuthHeader() }),
  getAuctionProperty: (propertyId) => axiosInstance.get(`/admin/supply/auctions/${propertyId}`, { headers: getAuthHeader() }),
  createAuctionProperty: (data) => axiosInstance.post(`/admin/supply/auctions`, data, { headers: getAuthHeader() }),
  updateAuctionProperty: (propertyId, data) => axiosInstance.put(`/admin/supply/auctions/${propertyId}`, data, { headers: getAuthHeader() }),
  verifyAuctionProperty: (propertyId) => axiosInstance.post(`/admin/supply/auctions/${propertyId}/verify`, {}, { headers: getAuthHeader() }),
  archiveAuctionProperty: (propertyId) => axiosInstance.post(`/admin/supply/auctions/${propertyId}/archive`, {}, { headers: getAuthHeader() }),
  markAuctionDuplicate: (propertyId, duplicateOf) => axiosInstance.post(`/admin/supply/auctions/${propertyId}/mark-duplicate?duplicate_of=${duplicateOf}`, {}, { headers: getAuthHeader() }),
  updateAuctionMarketPrice: (propertyId, price) => axiosInstance.post(`/admin/supply/auctions/${propertyId}/update-market-price?estimated_market_price=${price}`, {}, { headers: getAuthHeader() }),
  addAuctionNotes: (propertyId, notes) => axiosInstance.post(`/admin/supply/auctions/${propertyId}/add-notes?notes=${encodeURIComponent(notes)}`, {}, { headers: getAuthHeader() }),
  syncAuctionSupply: (source = 'all') => axiosInstance.post(`/admin/supply/auctions/sync`, { source }, { 
    headers: getAuthHeader(),
    timeout: 120000  // 2 minute timeout for sync operations
  }),
  getAuctionSyncLogs: (params = {}) => {
    const query = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([_, v]) => v != null))
    ).toString();
    return axiosInstance.get(`/admin/supply/auctions/sync/logs?${query}`, { headers: getAuthHeader() });
  },
  getLatestSyncStatus: () => axiosInstance.get(`/admin/supply/auctions/sync/latest`, { headers: getAuthHeader() }),
  getAuctionSources: () => axiosInstance.get(`/admin/supply/auctions/sources`, { headers: getAuthHeader() }),
  getConnectorHealth: (source = 'IBAPI') => axiosInstance.get(`/admin/supply/auctions/connector-health?source=${source}`, { headers: getAuthHeader() }),
  getDebugPayloads: (source = 'IBAPI') => axiosInstance.get(`/admin/supply/auctions/debug-payloads?source=${source}`, { headers: getAuthHeader() }),
  getAuctionAuditLogs: (params = {}) => {
    const query = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([_, v]) => v != null))
    ).toString();
    return axiosInstance.get(`/admin/supply/auctions/audit-logs?${query}`, { headers: getAuthHeader() });
  },
  getAuctionBanks: () => axiosInstance.get(`/admin/supply/auctions/lookup/banks`, { headers: getAuthHeader() }),
  getAuctionCities: () => axiosInstance.get(`/admin/supply/auctions/lookup/cities`, { headers: getAuthHeader() }),

  // Social Feed
  getPosts: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/feed/posts?${query}`, { headers: getAuthHeader() });
  },
  createPost: (data) => axiosInstance.post(`/feed/posts`, data, { headers: getAuthHeader() }),
  likePost: (postId) => axiosInstance.post(`/feed/posts/${postId}/like`, {}, { headers: getAuthHeader() }),
  reactToPost: (postId, reactionType) => axiosInstance.post(`/feed/posts/${postId}/react?reaction_type=${reactionType}`, {}, { headers: getAuthHeader() }),
  getComments: (postId) => axiosInstance.get(`/feed/posts/${postId}/comments`, { headers: getAuthHeader() }),
  createComment: (postId, data) => axiosInstance.post(`/feed/posts/${postId}/comments`, data, { headers: getAuthHeader() }),
  deletePost: (postId) => axiosInstance.delete(`/feed/posts/${postId}`, { headers: getAuthHeader() }),
  getTopContributors: () => axiosInstance.get(`/feed/top-contributors`, { headers: getAuthHeader() }),
  getMyEngagement: () => axiosInstance.get(`/feed/my-engagement`, { headers: getAuthHeader() }),

  // Notifications
  getNotifications: (params = {}) => {
    // Filter out null/undefined values before creating query string
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v != null)
    );
    const query = new URLSearchParams(filteredParams).toString();
    return axiosInstance.get(`/notifications${query ? `?${query}` : ''}`, { headers: getAuthHeader() });
  },
  getNotificationCount: () => axiosInstance.get(`/notifications/unread-count`, { headers: getAuthHeader() }),
  getHighPriorityNotifications: (sinceMinutes = 5) => 
    axiosInstance.get(`/notifications/high-priority?since_minutes=${sinceMinutes}`, { headers: getAuthHeader() }),
  markNotificationRead: (notificationId) => axiosInstance.put(`/notifications/${notificationId}/read`, {}, { headers: getAuthHeader() }),
  markAllNotificationsRead: (category = null) => {
    const query = category ? `?category=${category}` : '';
    return axiosInstance.put(`/notifications/mark-all-read${query}`, {}, { headers: getAuthHeader() });
  },
  dismissNotification: (notificationId) => axiosInstance.delete(`/notifications/${notificationId}`, { headers: getAuthHeader() }),
  clearLowPriorityNotifications: () => axiosInstance.delete(`/notifications/clear-low`, { headers: getAuthHeader() }),
  
  // User Search (for @mentions)
  searchUsers: (query) => axiosInstance.get(`/users/search?q=${encodeURIComponent(query)}`, { headers: getAuthHeader() }),

  // Chat
  getChatUsers: () => axiosInstance.get(`/chat/users`, { headers: getAuthHeader() }),
  getMessages: (userId) => axiosInstance.get(`/chat/messages/${userId}`, { headers: getAuthHeader() }),
  sendMessage: (data) => axiosInstance.post(`/chat/messages`, data, { headers: getAuthHeader() }),
  getUnreadCount: () => axiosInstance.get(`/chat/unread-count`, { headers: getAuthHeader() }),

  // Org Hierarchy
  getOrgHierarchy: () => axiosInstance.get(`/org/hierarchy`, { headers: getAuthHeader() }),
  getTeamPerformance: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/org/team-performance?${query}`, { headers: getAuthHeader() });
  },

  // Profile
  updateProfile: (data) => axiosInstance.put(`/auth/profile`, data, { headers: getAuthHeader() }),
  adminUpdateUserProfile: (userId, data) => axiosInstance.put(`/admin/users/${userId}/profile`, data, { headers: getAuthHeader() }),
  getAllUsers: () => axiosInstance.get(`/users`, { headers: getAuthHeader() }),
  getUsers: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/admin/users?${query}`, { headers: getAuthHeader() });
  },
  // Get team members (reps) for dropdowns - uses admin/users with role filter
  getTeamMembers: (params = {}) => {
    const query = new URLSearchParams({ ...params, limit: 200 }).toString();
    return axiosInstance.get(`/admin/users?${query}`, { headers: getAuthHeader() });
  },

  // CRM (V1 Rep-only - Site Visit Tracking)
  getCustomers: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/crm/customers?${query}`, { headers: getAuthHeader() });
  },
  getCRMCities: () => axiosInstance.get(`/crm/cities`, { headers: getAuthHeader() }),
  getCustomer: (customerId) => axiosInstance.get(`/crm/customers/${customerId}`, { headers: getAuthHeader() }),
  createCustomerWithVisit: (data) => axiosInstance.post(`/crm/customers`, data, { headers: getAuthHeader() }),
  updateCustomer: (customerId, data) => axiosInstance.put(`/crm/customers/${customerId}`, data, { headers: getAuthHeader() }),
  // Note: Reps cannot delete customers - removed
  
  // Visit Events - Lifecycle-based system
  scheduleVisit: (customerId, data) => axiosInstance.post(`/crm/customers/${customerId}/visits`, data, { headers: getAuthHeader() }),
  getCustomerVisits: (customerId) => axiosInstance.get(`/crm/customers/${customerId}/visits`, { headers: getAuthHeader() }),
  markVisitCompleted: (eventId, data) => axiosInstance.put(`/crm/visits/${eventId}/complete`, data, { headers: getAuthHeader() }),
  cancelVisit: (eventId, data) => axiosInstance.put(`/crm/visits/${eventId}/cancel`, data, { headers: getAuthHeader() }),
  rescheduleVisit: (eventId, data) => axiosInstance.put(`/crm/visits/${eventId}/reschedule`, data, { headers: getAuthHeader() }),
  getVisitCancelReasons: () => axiosInstance.get(`/crm/visits/cancel-reasons`, { headers: getAuthHeader() }),
  
  // Follow-ups
  createFollowup: (customerId, data) => axiosInstance.post(`/crm/customers/${customerId}/followups`, data, { headers: getAuthHeader() }),
  completeFollowup: (followupId, data) => axiosInstance.put(`/crm/followups/${followupId}/complete`, data, { headers: getAuthHeader() }),
  rescheduleFollowup: (followupId, data) => axiosInstance.put(`/crm/followups/${followupId}/reschedule`, data, { headers: getAuthHeader() }),
  getFollowups: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/crm/followups?${query}`, { headers: getAuthHeader() });
  },
  
  // CRM Actions
  markCustomerLost: (customerId, data) => axiosInstance.post(`/crm/customers/${customerId}/mark-lost`, data, { headers: getAuthHeader() }),
  createBookingFromCRM: (customerId, data) => axiosInstance.post(`/crm/customers/${customerId}/booking`, data, { headers: getAuthHeader() }),
  
  // Customer Notes
  addCustomerNote: (customerId, data) => axiosInstance.post(`/crm/customers/${customerId}/notes`, data, { headers: getAuthHeader() }),
  getCustomerNotes: (customerId, limit = 50) => axiosInstance.get(`/crm/customers/${customerId}/notes?limit=${limit}`, { headers: getAuthHeader() }),
  
  // Smart Note to WhatsApp
  convertNoteToWhatsApp: (data) => axiosInstance.post('/crm/smart-note/convert', data, { headers: getAuthHeader() }),
  
  // SAIRA - AI Sales Coach (longer timeout for AI responses)
  sairaCoach: (data, config = {}) => axiosInstance.post('/saira/coach', data, { 
    headers: getAuthHeader(), 
    timeout: 60000,
    ...config 
  }),
  getSairaHistory: (leadId = null, limit = 20) => {
    const params = new URLSearchParams({ limit });
    if (leadId) params.append('lead_id', leadId);
    return axiosInstance.get(`/saira/history?${params}`, { headers: getAuthHeader() });
  },
  trackSairaShare: (conversationId, shareType) => 
    axiosInstance.post(`/saira/track-share?conversation_id=${conversationId}&share_type=${shareType}`, {}, { headers: getAuthHeader() }),
  getSairaAnalytics: (days = 30) => axiosInstance.get(`/admin/saira/analytics?days=${days}`, { headers: getAuthHeader() }),
  
  // User Preferences
  getUserPreferences: () => axiosInstance.get('/user/preferences', { headers: getAuthHeader() }),
  updateUserPreferences: (data) => axiosInstance.patch('/user/preferences', data, { headers: getAuthHeader() }),
  
  // AV-Based Incentive System
  getAVMonthlySummary: (month = null) => {
    const params = month ? `?month=${month}` : '';
    return axiosInstance.get(`/incentive/av/monthly-summary${params}`, { headers: getAuthHeader() });
  },
  getAVMyBookings: (month = null, statusFilter = null) => {
    const params = new URLSearchParams();
    if (month) params.append('month', month);
    if (statusFilter) params.append('status_filter', statusFilter);
    const queryString = params.toString();
    return axiosInstance.get(`/incentive/av/my-bookings${queryString ? `?${queryString}` : ''}`, { headers: getAuthHeader() });
  },
  getAVAvailableMonths: () => axiosInstance.get('/incentive/av/available-months', { headers: getAuthHeader() }),
  
  // Enhanced My Bookings with Time Range
  getMyBookingsEnhanced: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.range) queryParams.append('range', params.range);
    if (params.start) queryParams.append('start', params.start);
    if (params.end) queryParams.append('end', params.end);
    if (params.status) queryParams.append('status', params.status);
    const queryString = queryParams.toString();
    return axiosInstance.get(`/bookings/my${queryString ? `?${queryString}` : ''}`, { headers: getAuthHeader() });
  },
  
  // First Visit Cohort Analysis
  getCohortAnalysis: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.granularity) queryParams.append('granularity', params.granularity);
    if (params.city) queryParams.append('city', params.city);
    if (params.periods) queryParams.append('periods', params.periods);
    const queryString = queryParams.toString();
    return axiosInstance.get(`/admin/cohort-analysis/first-visit${queryString ? `?${queryString}` : ''}`, { headers: getAuthHeader() });
  },
  
  // CRM Stats
  getCRMStats: (params = {}) => {
    const queryParams = new URLSearchParams();
    // Pass all params to ensure HWC counts match filtered view
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });
    const queryString = queryParams.toString();
    return axiosInstance.get(`/crm/stats${queryString ? `?${queryString}` : ''}`, { headers: getAuthHeader() });
  },
  
  // Referral Tracking
  createReferral: (customerId, data) => axiosInstance.post(`/crm/customers/${customerId}/referral`, data, { headers: getAuthHeader() }),
  addReferralLead: (customerId, data) => axiosInstance.post(`/crm/customers/${customerId}/add-referral`, data, { headers: getAuthHeader() }),
  getReferral: (customerId) => axiosInstance.get(`/crm/customers/${customerId}/referral`, { headers: getAuthHeader() }),
  searchReferrerCustomers: (query) => axiosInstance.get(`/crm/customers/search-referrer?q=${encodeURIComponent(query)}`, { headers: getAuthHeader() }),
  searchCustomersForTask: (query) => axiosInstance.get(`/crm/customers/search-for-task?q=${encodeURIComponent(query)}`, { headers: getAuthHeader() }),
  adminUpdateReferral: (customerId, data, reason) => axiosInstance.put(`/admin/crm/customers/${customerId}/referral?reason=${encodeURIComponent(reason)}`, data, { headers: getAuthHeader() }),
  adminRejectReferral: (customerId, reason) => axiosInstance.delete(`/admin/crm/customers/${customerId}/referral?reason=${encodeURIComponent(reason)}`, { headers: getAuthHeader() }),
  getReferralDashboard: (period = 'MTD', city = null) => axiosInstance.get(`/referral/dashboard?period=${period}${city ? `&city=${city}` : ''}`, { headers: getAuthHeader() }),
  getReferralAuditLog: (referralId) => axiosInstance.get(`/referral/audit-log/${referralId}`, { headers: getAuthHeader() }),
  updateReferralPayoutStatus: (referralId, status, remarks) => axiosInstance.put(`/admin/referral/payout/${referralId}/status?status=${status}${remarks ? `&remarks=${encodeURIComponent(remarks)}` : ''}`, {}, { headers: getAuthHeader() }),
  
  // VDNB Management
  getVDNBDashboard: (city = null) => axiosInstance.get(`/vdnb/dashboard${city ? `?city=${city}` : ''}`, { headers: getAuthHeader() }),
  getVDNBCustomers: (params = {}) => axiosInstance.get(`/vdnb/customers`, { params, headers: getAuthHeader() }),
  reassignVDNBCustomer: (customerId, newRepUserId) => axiosInstance.post(`/vdnb/reassign/${customerId}?new_rep_user_id=${newRepUserId}`, {}, { headers: getAuthHeader() }),
  resetOwnershipClock: (customerId, reason) => axiosInstance.post(`/vdnb/reset-ownership-clock/${customerId}?reason=${encodeURIComponent(reason)}`, {}, { headers: getAuthHeader() }),
  triggerVDNBJob: () => axiosInstance.post(`/vdnb/trigger-nightly-job`, {}, { headers: getAuthHeader() }),
  getVDNBJobHistory: (limit = 10) => axiosInstance.get(`/vdnb/job-history?limit=${limit}`, { headers: getAuthHeader() }),
  
  // Admin Command Center
  getCommandCenter: (city = null, period = 'MTD') => axiosInstance.get(`/admin/command-center?period=${period}${city ? `&city=${city}` : ''}`, { headers: getAuthHeader() }),
  getPipelineHygiene: (city = null) => axiosInstance.get(`/admin/pipeline-hygiene${city ? `?city=${city}` : ''}`, { headers: getAuthHeader() }),
  getRiskRadar: (city = null) => axiosInstance.get(`/admin/risk-radar${city ? `?city=${city}` : ''}`, { headers: getAuthHeader() }),
  getAdminAlerts: () => axiosInstance.get(`/admin/alerts`, { headers: getAuthHeader() }),
  getAuditLogs: (params = {}) => axiosInstance.get(`/admin/audit-logs`, { params, headers: getAuthHeader() }),
  
  // Quarter Freeze
  freezeQuarter: (quarter, fiscalYear) => axiosInstance.post(`/admin/incentive/freeze-quarter?quarter=${quarter}&fiscal_year=${fiscalYear}`, {}, { headers: getAuthHeader() }),
  unfreezeQuarter: (quarter, fiscalYear, reason) => axiosInstance.post(`/admin/incentive/unfreeze-quarter?quarter=${quarter}&fiscal_year=${fiscalYear}&reason=${encodeURIComponent(reason)}`, {}, { headers: getAuthHeader() }),
  getQuarterStatus: () => axiosInstance.get(`/admin/incentive/quarter-status`, { headers: getAuthHeader() }),
  
  // Booking Funnel Dashboard
  getBookingFunnelDashboard: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.period) queryParams.append('period', params.period);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.city) queryParams.append('city', params.city);
    const queryString = queryParams.toString();
    return axiosInstance.get(`/admin/booking-funnel-dashboard${queryString ? `?${queryString}` : ''}`, { headers: getAuthHeader() });
  },
  getBookingFunnelBookings: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.stage) queryParams.append('stage', params.stage);
    if (params.period) queryParams.append('period', params.period);
    if (params.city) queryParams.append('city', params.city);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    const queryString = queryParams.toString();
    return axiosInstance.get(`/admin/booking-funnel-bookings${queryString ? `?${queryString}` : ''}`, { headers: getAuthHeader() });
  },
  getCancellationHistory: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.city) queryParams.append('city', params.city);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    const queryString = queryParams.toString();
    return axiosInstance.get(`/admin/cancellation-history${queryString ? `?${queryString}` : ''}`, { headers: getAuthHeader() });
  },
  exportCancellationHistory: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.city) queryParams.append('city', params.city);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    const queryString = queryParams.toString();
    return axiosInstance.get(`/admin/cancellation-history/export${queryString ? `?${queryString}` : ''}`, { 
      headers: getAuthHeader(),
      responseType: 'blob'
    });
  },
  updateBookingMilestone: (bookingId, milestone, data = {}) => axiosInstance.post(`/bookings/${bookingId}/milestone`, { milestone, ...data }, { headers: getAuthHeader() }),
  clearBookingMilestone: (bookingId, milestone) => axiosInstance.delete(`/bookings/${bookingId}/milestone/${milestone}`, { headers: getAuthHeader() }),
  
  // Booking Cancellation
  cancelBooking: (bookingId, data) => axiosInstance.post(`/bookings/${bookingId}/cancel`, data, { headers: getAuthHeader() }),
  reinstateBooking: (bookingId) => axiosInstance.post(`/bookings/${bookingId}/reinstate`, {}, { headers: getAuthHeader() }),
  
  // Unified Booking Approval (3-tier) - Legacy
  cityApproveBooking: (bookingId, data = {}) => axiosInstance.post(`/bookings/${bookingId}/city-approve`, data, { headers: getAuthHeader() }),
  strategyApproveBooking: (bookingId, data = {}) => axiosInstance.post(`/bookings/${bookingId}/strategy-approve`, data, { headers: getAuthHeader() }),
  rejectBookingToRep: (bookingId, reason) => axiosInstance.post(`/bookings/${bookingId}/reject-to-rep`, { reason }, { headers: getAuthHeader() }),
  cancelBookingPostApproval: (bookingId, data) => axiosInstance.post(`/bookings/${bookingId}/cancel-post-approval`, data, { headers: getAuthHeader() }),
  
  // NEW: Maker-Checker Booking Workflow
  // Actions: city_verify, raise_query, strategy_approve, strategy_query
  bookingVerifyAction: (bookingId, action, remark = null) => {
    const params = new URLSearchParams({ action });
    if (remark) params.append('remark', remark);
    return axiosInstance.post(`/bookings/${bookingId}/verify-action?${params.toString()}`, {}, { headers: getAuthHeader() });
  },
  
  // City Head can edit minor errors and verify in one action
  cityHeadEditVerify: (bookingId, data) => axiosInstance.post(`/bookings/${bookingId}/city-head-edit-verify`, data, { headers: getAuthHeader() }),
  
  // CA/Rep can edit and resubmit booking after query is raised
  caResubmitBooking: (bookingId, data) => axiosInstance.put(`/bookings/${bookingId}/ca-resubmit`, data, { headers: getAuthHeader() }),
  
  // Inline edit booking fields (Strategy Support / Super Admin only)
  inlineEditBooking: (bookingId, data) => axiosInstance.put(`/bookings/${bookingId}/edit`, data, { headers: getAuthHeader() }),
  
  // Upload document for existing booking (cost sheet, booking form, payment proof)
  uploadExistingBookingDocument: (bookingId, docType, file, remarks = '') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('doc_type', docType);
    if (remarks) formData.append('remarks', remarks);
    return axiosInstance.post(`/bookings/${bookingId}/upload-document`, formData, {
      headers: getAuthHeader()  // Don't set Content-Type - axios handles it for FormData
    });
  },
  
  // Data Migration
  migrateUnifiedDataModel: () => axiosInstance.post(`/admin/migrate-unified-data-model`, {}, { headers: getAuthHeader() }),

  // ==================== BD DESK ====================
  
  // BD Access
  checkBDAccess: () => axiosInstance.get('/bd/access-check', { headers: getAuthHeader() }),
  
  // BD Dashboard
  getBDDashboard: () => axiosInstance.get('/bd/dashboard', { headers: getAuthHeader() }),
  
  // Builders
  getBuilders: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/bd/builders?${query}`, { headers: getAuthHeader() });
  },
  getBuilder: (builderId) => axiosInstance.get(`/bd/builders/${builderId}`, { headers: getAuthHeader() }),
  createBuilder: (data) => axiosInstance.post('/bd/builders', data, { headers: getAuthHeader() }),
  updateBuilder: (builderId, data) => axiosInstance.put(`/bd/builders/${builderId}`, data, { headers: getAuthHeader() }),
  deleteBuilder: (builderId) => axiosInstance.delete(`/bd/builders/${builderId}`, { headers: getAuthHeader() }),
  
  // BD Projects
  getBDProjects: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/bd/projects?${query}`, { headers: getAuthHeader() });
  },
  getBDPipeline: (builderId = null) => {
    const query = builderId ? `?builder_id=${builderId}` : '';
    return axiosInstance.get(`/bd/projects/pipeline${query}`, { headers: getAuthHeader() });
  },
  getBDProject: (projectId) => axiosInstance.get(`/bd/projects/${projectId}`, { headers: getAuthHeader() }),
  createBDProject: (data) => axiosInstance.post('/bd/projects', data, { headers: getAuthHeader() }),
  updateBDProject: (projectId, data) => axiosInstance.put(`/bd/projects/${projectId}`, data, { headers: getAuthHeader() }),
  updateBDProjectStage: (projectId, newStage) => axiosInstance.put(`/bd/projects/${projectId}/stage`, { new_stage: newStage }, { headers: getAuthHeader() }),
  deleteBDProject: (projectId) => axiosInstance.delete(`/bd/projects/${projectId}`, { headers: getAuthHeader() }),
  
  // BD Meetings
  getBDMeetings: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/bd/meetings?${query}`, { headers: getAuthHeader() });
  },
  getBDMeeting: (meetingId) => axiosInstance.get(`/bd/meetings/${meetingId}`, { headers: getAuthHeader() }),
  createBDMeeting: (data) => axiosInstance.post('/bd/meetings', data, { headers: getAuthHeader() }),
  updateBDMeeting: (meetingId, data) => axiosInstance.put(`/bd/meetings/${meetingId}`, data, { headers: getAuthHeader() }),
  deleteBDMeeting: (meetingId) => axiosInstance.delete(`/bd/meetings/${meetingId}`, { headers: getAuthHeader() }),
  
  // BD Lookups
  getBDCities: () => axiosInstance.get('/bd/lookup/cities', { headers: getAuthHeader() }),
  getBDOwners: () => axiosInstance.get('/bd/lookup/bd-owners', { headers: getAuthHeader() }),
  
  // BD CSV Import
  getBuilderImportTemplate: () => axiosInstance.get('/bd/builders-import/template', { headers: getAuthHeader() }),
  importBuildersCSV: (data) => axiosInstance.post('/bd/builders-import/csv', data, { headers: getAuthHeader() }),
  getBDProjectImportTemplate: () => axiosInstance.get('/bd/projects-import/template', { headers: getAuthHeader() }),
  importBDProjectsCSV: (data) => axiosInstance.post('/bd/projects-import/csv', data, { headers: getAuthHeader() }),
  
  // BD Analytics
  getBDBuilderRanking: (period = 'fytd') => axiosInstance.get(`/bd/analytics/builder-ranking?period=${period}`, { headers: getAuthHeader() }),
  getBDCollectionAging: () => axiosInstance.get('/bd/analytics/collection-aging', { headers: getAuthHeader() }),
  getBDPipelineVelocity: () => axiosInstance.get('/bd/analytics/pipeline-velocity', { headers: getAuthHeader() }),
  
  // BD Revenue Pipeline
  getBDRevenueSummary: (period = 'mtd') => axiosInstance.get(`/bd/revenue/summary?period=${period}`, { headers: getAuthHeader() }),
  getBDRevenueAging: () => axiosInstance.get('/bd/revenue/aging', { headers: getAuthHeader() }),
  getBDRevenuePerformance: () => axiosInstance.get('/bd/revenue/performance', { headers: getAuthHeader() }),
  getBDRevenueAlerts: () => axiosInstance.get('/bd/revenue/alerts', { headers: getAuthHeader() }),
  getBDRevenueBuilderCases: (builderId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/bd/revenue/builder/${builderId}?${query}`, { headers: getAuthHeader() });
  },
  getBDRevenueCaseDetail: (caseId) => axiosInstance.get(`/bd/revenue/case/${caseId}`, { headers: getAuthHeader() }),
  updateBDRevenueCaseStage: (caseId, data) => axiosInstance.post(`/bd/revenue/case/${caseId}/update-stage`, data, { headers: getAuthHeader() }),
  backfillBrokerageCases: () => axiosInstance.post('/bd/revenue/backfill', {}, { headers: getAuthHeader() }),
  
  // Revenue Cases Tab APIs
  getAllBrokerageCases: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/bd/revenue/cases?${query}`, { headers: getAuthHeader() });
  },
  bulkUpdateBrokerageCases: (data) => axiosInstance.post('/bd/revenue/cases/bulk-update', data, { headers: getAuthHeader() }),
  addPartialPayment: (caseId, data) => axiosInstance.post(`/bd/revenue/case/${caseId}/payment`, data, { headers: getAuthHeader() }),
  getBrokerageCaseWithHistory: (caseId) => axiosInstance.get(`/bd/revenue/case/${caseId}/detail`, { headers: getAuthHeader() }),
  
  generateConsolidatedInvoice: (builderId, caseIds, invoiceNumber, invoiceDate) => {
    const params = new URLSearchParams({
      builder_id: builderId,
      case_ids: caseIds.join(','),
      invoice_number: invoiceNumber,
      invoice_date: invoiceDate
    }).toString();
    return axiosInstance.post(`/bd/revenue/generate-consolidated-invoice?${params}`, {}, { headers: getAuthHeader() });
  },
  updateBuilderPolicy: (builderId, data) => axiosInstance.put(`/bd/builders/${builderId}/policy`, data, { headers: getAuthHeader() }),
  
  // Market Intelligence
  getMarketNews: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/market-news?${query}`, { headers: getAuthHeader() });
  },
  getMarketPulse: () => axiosInstance.get('/market-news/pulse', { headers: getAuthHeader() }),
  getMarketNewsStats: () => axiosInstance.get('/market-news/stats', { headers: getAuthHeader() }),
  refreshMarketNews: () => axiosInstance.post('/market-news/refresh', {}, { headers: getAuthHeader() }),
  rebuildMarketSummaries: (limit = 30) => axiosInstance.post(`/market-news/rebuild-summaries?limit=${limit}`, {}, { headers: getAuthHeader() }),
  
  // === BOOKING MASTER ENGINE ===
  // City Head Dashboard
  getCityHeadDashboard: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/booking-master/city-head-dashboard?${query}`, { headers: getAuthHeader() });
  },
  // Visits List with filters
  getVisitsList: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/crm/visits/list?${query}`, { headers: getAuthHeader() });
  },
  // Manager Reps breakdown (for City Head drilldown)
  getManagerReps: (managerId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/booking-master/manager/${managerId}/reps?${query}`, { headers: getAuthHeader() });
  },
  // Manager Funnel Table (spreadsheet view)
  getManagerFunnelTable: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/booking-master/manager-funnel-table?${query}`, { headers: getAuthHeader() });
  },
  // Manager Funnel Table Rep Drilldown
  getManagerFunnelTableReps: (managerId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/booking-master/manager-funnel-table/${managerId}/reps?${query}`, { headers: getAuthHeader() });
  },
  // Manager Dashboard
  getManagerDashboard: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/booking-master/manager-dashboard?${query}`, { headers: getAuthHeader() });
  },
  // Unified Booking List
  getUnifiedBookingList: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axiosInstance.get(`/booking-master/unified-booking-list?${query}`, { headers: getAuthHeader() });
  },
  // Update Booking Status
  updateBookingMasterStatus: (bookingId, newStatus, notes) => {
    const params = new URLSearchParams({ new_status: newStatus });
    if (notes) params.append('notes', notes);
    return axiosInstance.put(`/booking-master/update-status/${bookingId}?${params}`, {}, { headers: getAuthHeader() });
  },
  // Migrate Legacy Statuses
  migrateBookingStatuses: (dryRun = true) => {
    return axiosInstance.post(`/booking-master/migrate-statuses?dry_run=${dryRun}`, {}, { headers: getAuthHeader() });
  },
  
  // ==================== Lost Review Pool APIs ====================
  
  // Get Lost Review Pool (City Head view)
  getLostReviewPool: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.city) queryParams.append('city', params.city);
    if (params.aging_filter) queryParams.append('aging_filter', params.aging_filter);
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.skip) queryParams.append('skip', params.skip);
    if (params.limit) queryParams.append('limit', params.limit);
    return axiosInstance.get(`/admin/lost-review-pool?${queryParams}`, { headers: getAuthHeader() });
  },
  
  // Process Lost Review Action
  processLostReviewAction: (customerId, action, remark, newRepUserId, newStage) => {
    const payload = { action };
    if (remark) payload.remark = remark;
    if (newRepUserId) payload.new_rep_user_id = newRepUserId;
    if (newStage) payload.new_stage = newStage;
    return axiosInstance.post(`/admin/lost-review/${customerId}/action`, payload, { headers: getAuthHeader() });
  },
  
  // Get Lost Recovery Metrics
  getLostRecoveryMetrics: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.period) queryParams.append('period', params.period);
    if (params.city) queryParams.append('city', params.city);
    return axiosInstance.get(`/admin/lost-recovery-metrics?${queryParams}`, { headers: getAuthHeader() });
  },
  
  // Export axiosInstance for direct use
  axiosInstance,
  
  // ==================== Manager Desk APIs ====================
  
  // Tasks
  getManagerTasks: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.assigned_to) queryParams.append('assigned_to', params.assigned_to);
    if (params.status) queryParams.append('status', params.status);
    if (params.priority) queryParams.append('priority', params.priority);
    if (params.due_date) queryParams.append('due_date', params.due_date);
    if (params.search) queryParams.append('search', params.search);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    return axiosInstance.get(`/manager-desk/tasks?${queryParams}`, { headers: getAuthHeader() });
  },
  
  getManagerTasksSummary: () => axiosInstance.get('/manager-desk/tasks/summary', { headers: getAuthHeader() }),
  
  createManagerTask: (task) => axiosInstance.post('/manager-desk/tasks', task, { headers: getAuthHeader() }),
  
  updateManagerTask: (taskId, task) => axiosInstance.put(`/manager-desk/tasks/${taskId}`, task, { headers: getAuthHeader() }),
  
  deleteManagerTask: (taskId) => axiosInstance.delete(`/manager-desk/tasks/${taskId}`, { headers: getAuthHeader() }),
  
  getManagerTeamMembers: () => axiosInstance.get('/manager-desk/team-members', { headers: getAuthHeader() }),
  
  // Followups
  getManagerFollowups: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.assigned_rep) queryParams.append('assigned_rep', params.assigned_rep);
    if (params.due_date) queryParams.append('due_date', params.due_date);
    if (params.search) queryParams.append('search', params.search);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    return axiosInstance.get(`/manager-desk/followups?${queryParams}`, { headers: getAuthHeader() });
  },
  
  createManagerFollowup: (followup) => axiosInstance.post('/manager-desk/followups', followup, { headers: getAuthHeader() }),
  
  updateManagerFollowup: (followupId, followup) => axiosInstance.put(`/manager-desk/followups/${followupId}`, followup, { headers: getAuthHeader() }),
  
  deleteManagerFollowup: (followupId) => axiosInstance.delete(`/manager-desk/followups/${followupId}`, { headers: getAuthHeader() }),
  
  // Diary / Notes
  getManagerDiary: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.tag) queryParams.append('tag', params.tag);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    return axiosInstance.get(`/manager-desk/diary?${queryParams}`, { headers: getAuthHeader() });
  },
  
  createManagerDiaryNote: (note) => axiosInstance.post('/manager-desk/diary', note, { headers: getAuthHeader() }),
  
  updateManagerDiaryNote: (noteId, note) => axiosInstance.put(`/manager-desk/diary/${noteId}`, note, { headers: getAuthHeader() }),
  
  deleteManagerDiaryNote: (noteId) => axiosInstance.delete(`/manager-desk/diary/${noteId}`, { headers: getAuthHeader() }),
  
  // ==================== My Tasks (Rep-Accessible) ====================
  
  getMyTasks: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.priority) queryParams.append('priority', params.priority);
    if (params.due_date) queryParams.append('due_date', params.due_date);
    if (params.search) queryParams.append('search', params.search);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    return axiosInstance.get(`/my-tasks?${queryParams}`, { headers: getAuthHeader() });
  },
  
  getMyTasksSummary: () => axiosInstance.get('/my-tasks/summary', { headers: getAuthHeader() }),
  
  createMyTask: (task) => axiosInstance.post('/my-tasks', task, { headers: getAuthHeader() }),
  
  updateMyTask: (taskId, task) => axiosInstance.put(`/my-tasks/${taskId}`, task, { headers: getAuthHeader() }),
  
  deleteMyTask: (taskId) => axiosInstance.delete(`/my-tasks/${taskId}`, { headers: getAuthHeader() }),

  // ==================== Booking Edit Lock ====================
  
  acquireBookingLock: (bookingId) => axiosInstance.post(`/bookings/${bookingId}/acquire-lock`, {}, { headers: getAuthHeader() }),
  
  releaseBookingLock: (bookingId) => axiosInstance.post(`/bookings/${bookingId}/release-lock`, {}, { headers: getAuthHeader() }),
  
  getBookingLockStatus: (bookingId) => axiosInstance.get(`/bookings/${bookingId}/lock-status`, { headers: getAuthHeader() }),
};

// Default export for convenience
export default api;
